import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { Row, Col, Avatar, Form } from 'antd'
import { useSelector, useDispatch } from 'react-redux'
import { FormSection } from '../../../../core/components/FormSection.tsx'
import { InputManager } from '../../../../core/components/InputManager/index.ts'
import { updateContactRecord } from '../../../../state_manager/state_update_actions/contacts/index.ts'
import { ContactRecordFields, ContactRecord } from '../../../../state_manager/data_models/contact_record.ts'
import {
  setActiveRecord,
  registerRecordUsage,
  unregisterRecordUsage
} from '../../../../state_manager/state_update_actions/active_records/active_record_actions.ts'
import { selectActiveRecordById } from '../../../../state_manager/state_update_actions/active_records/selectors.ts'
import { getContactInfoFields, getAddressFields } from '../../utils/fieldConfigs.ts'
import { handleFieldChange as handleBusinessRules } from '../../utils/contactFormBusinessRules.ts'
import { useLookups } from '../../../global_configs/hooks/useLookups.ts'
import { ContactRolesTable } from '../tables/ContactRolesTable.tsx'
import { RightMenuContext } from '../../../../core/contexts/RightMenuContext.jsx'
import { SECTION_CONTACT_OVERVIEW } from '../../../../state_manager/constants/strings.ts'
import { useContext, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import {
  processContactCompanyRelationships,
  transformFounderOfToAPIFormat,
  fetchContactById
} from '../../services/contactsService.ts'
import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../../core/utils/endpoints.ts'
import { toast } from 'react-toastify'
import { showErrorToast } from '../../../../core/utils/errorToast.tsx'
import { TOAST_CONFIG, TOAST_AUTO_CLOSE } from '../../../../core/components/FormTable/constants.ts'
import { getAccessToken } from '../../../../core/auth/authService'
import { store } from '../../../../state_manager/store.ts'
import { IntroductionsTable } from '../../../../core/components/IntroductionsTable/index.jsx'
import { CompanyTeamEntryFields, CompanyTeamEntry } from '../../../../state_manager/data_models/company_team_entry.ts'
import { ACTION_TYPE_INSERT, ACTION_TYPE_DELETE, ACTION_TYPE_UPDATE } from '../../../../state_manager/globals.ts'

export function Drawer({
  contact = null,
  currentRolesKey,
  priorRolesKey,
  introductionsKey
}: {
  contact?: ContactRecord | null
  onSubmit?: (data: any) => Promise<void>
  loading?: boolean
  currentRolesKey?: string | undefined
  priorRolesKey?: string | undefined
  introductionsKey?: string | undefined
}) {
  const dispatch = useDispatch()
  const location = useLocation()

  const isDrawerContext = location.pathname.includes('/forms/drawer/')

  const rightMenuContext = useContext(RightMenuContext)
  const { setRightMenuWidth, resetRightMenuWidth } =
    isDrawerContext && rightMenuContext
      ? rightMenuContext
      : { setRightMenuWidth: () => {}, resetRightMenuWidth: () => {} }

  const { getLookupOptions } = useLookups()

  const filterOptions = useSelector((state: any) => state.contacts?.filterOptions)
  const relationshipCategories = useSelector((state: any) => state.globalConfigs?.relationship_categories || [])
  const countriesData = useSelector((state: any) => state.globalConfigs?.countries || [])

  const contactId = contact?.[ContactRecordFields.contacts__id]

  const contactFromActiveRecords = useSelector((state: any) =>
    contactId ? selectActiveRecordById(state, contactId) : null
  )
  const currentContact = contactFromActiveRecords || contact

  const [form] = Form.useForm()

  const [formData, setFormData] = useState<Record<string, any>>({})

  const introductionsRef = useRef<any>(null)

  useEffect(() => {
    if (isDrawerContext) {
      setRightMenuWidth('42%')
      return () => {
        resetRightMenuWidth()
      }
    }
    return undefined
  }, [isDrawerContext, setRightMenuWidth, resetRightMenuWidth])

  const previousContactIdRef = useRef<string | null | undefined>(null)

  useEffect(() => {
    const contactIdString = contactId?.toString()
    const previousContactIdString = previousContactIdRef.current?.toString()

    if (contactIdString && contactIdString !== previousContactIdString && contact) {
      dispatch(setActiveRecord({ uuid: contactIdString, record: contact }))
      dispatch(registerRecordUsage({ uuid: contactIdString, context: 'drawer' }))
      previousContactIdRef.current = contactId
    }

    return () => {
      if (previousContactIdString) {
        dispatch(unregisterRecordUsage({ uuid: previousContactIdString, context: 'drawer' }))
      }
    }
  }, [contactId, dispatch])

  useEffect(() => {
    if (currentContact) {
      const initialData: Record<string, any> = {
        ...currentContact
      }
      setFormData(initialData)
    }
  }, [currentContact])

  const handleIntroductionsSave = useCallback(
    async (savedIntro: any, allIntroductions: any[]) => {
      const currentToken = getAccessToken(store.getState().auth)
      if (!contactId || !currentToken) {
        showErrorToast('Cannot save introductions: Contact ID or access token not available', {
          ...TOAST_CONFIG,
          autoClose: TOAST_AUTO_CLOSE.DEFAULT
        })
        return
      }

      try {
        const currentIntroductions = contact?.prog__introductions || formData.prog__introductions || []

        const currentIntroMapById = new Map<string, any>()
        currentIntroductions.forEach((intro: any) => {
          const introId = intro[CompanyTeamEntryFields.contact_accounts__id] || intro.contact_accounts__id
          if (introId) {
            currentIntroMapById.set(introId, intro)
          }
        })

        const newIntroductionsData = allIntroductions
          .filter((intro) => intro.companyId && intro.categoryId)
          .map((intro) => {
            const isNew = intro.key.startsWith('new-')
            const introId = isNew ? null : intro.key
            const originalIntro = introId ? currentIntroMapById.get(introId) : null

            let action = ''
            let finalIntroId = introId
            if (!isNew && originalIntro) {
              const originalCompanyId =
                originalIntro[CompanyTeamEntryFields.contact_accounts__account_id] ||
                originalIntro.prog__account_id ||
                originalIntro.account_id
              const originalCategoryId =
                originalIntro[CompanyTeamEntryFields.contact_accounts__relationship_category_id] ||
                originalIntro.contact_accounts__relationship_category_id

              if (originalCompanyId !== intro.companyId || originalCategoryId !== intro.categoryId) {
                action = ACTION_TYPE_INSERT
                finalIntroId = null
              }
            } else if (isNew) {
              action = ACTION_TYPE_INSERT
            }

            return {
              [CompanyTeamEntryFields.contacts__id]: contactId,
              [CompanyTeamEntryFields.contact_accounts__id]: finalIntroId,
              [CompanyTeamEntryFields.contact_accounts__account_id]: intro.companyId,
              [CompanyTeamEntryFields.contact_accounts__relationship_category_id]: intro.categoryId,
              [CompanyTeamEntryFields.prog__contact_accounts__action]: action,
              [CompanyTeamEntryFields.prog__founder]: false
            }
          })

        const relationshipsByCompany = new Map<string, any[]>()

        const currentIntroMap = new Map<string, any>()
        currentIntroductions.forEach((intro: any) => {
          const companyId =
            intro[CompanyTeamEntryFields.contact_accounts__account_id] || intro.prog__account_id || intro.account_id
          const categoryId =
            intro[CompanyTeamEntryFields.contact_accounts__relationship_category_id] ||
            intro.contact_accounts__relationship_category_id
          const introId = intro[CompanyTeamEntryFields.contact_accounts__id] || intro.contact_accounts__id
          if (companyId && categoryId && introId) {
            currentIntroMap.set(`${companyId}-${categoryId}`, { companyId, introId, categoryId })
          }
        })

        const newIntroMap = new Map<string, any>()
        const newIntroMapByIntroId = new Map<string, any>()
        newIntroductionsData.forEach((intro) => {
          const companyId = intro[CompanyTeamEntryFields.contact_accounts__account_id]
          const categoryId = intro[CompanyTeamEntryFields.contact_accounts__relationship_category_id]
          const introId = intro[CompanyTeamEntryFields.contact_accounts__id]
          const action = intro[CompanyTeamEntryFields.prog__contact_accounts__action]

          if (companyId && categoryId) {
            newIntroMap.set(`${companyId}-${categoryId}`, intro)

            if (introId) {
              newIntroMapByIntroId.set(introId, intro)
            }

            if (action === ACTION_TYPE_INSERT || action === ACTION_TYPE_UPDATE) {
              const relationships = relationshipsByCompany.get(companyId) || []
              relationships.push(intro)
              relationshipsByCompany.set(companyId, relationships)
            }
          }
        })

        currentIntroMap.forEach((currentIntro, key) => {
          if (!newIntroMap.has(key)) {
            const relationships = relationshipsByCompany.get(currentIntro.companyId) || []
            relationships.push({
              [CompanyTeamEntryFields.contact_accounts__id]: currentIntro.introId,
              [CompanyTeamEntryFields.prog__contact_accounts__action]: ACTION_TYPE_DELETE
            })
            relationshipsByCompany.set(currentIntro.companyId, relationships)
          }
        })

        const companiesToProcess: Array<{ companyId: string; relationships: any[] }> = []
        for (const [companyId, relationships] of relationshipsByCompany.entries()) {
          if (relationships.length > 0) {
            companiesToProcess.push({ companyId, relationships })
          }
        }

        if (companiesToProcess.length === 0) {
          setFormData((prev) => ({
            ...prev,
            prog__introductions: newIntroductionsData
          }))
          return
        }

        const results = await Promise.allSettled(
          companiesToProcess.map(({ companyId, relationships }) =>
            processContactCompanyRelationships(contactId, companyId, relationships, currentToken)
          )
        )

        const failures = results.filter(
          (result) => result.status === 'rejected' || (result.status === 'fulfilled' && !result.value.success)
        )
        if (failures.length > 0) {
          const errorMessages = failures.map((failure) => {
            if (failure.status === 'rejected') {
              return failure.reason?.message || 'Unknown error'
            }
            return failure.value.error || 'Unknown error'
          })
          showErrorToast(`Failed to update some introductions: ${errorMessages.join(', ')}`, {
            ...TOAST_CONFIG
          })
          return
        }

        const response = await fetchContactById(contactId, currentToken)
        if (response.data && !response.error) {
          dispatch(updateContactRecord({ contactId, updates: response.data }))
          setFormData(response.data)
        }

        toast.success('Introductions updated successfully', {
          ...TOAST_CONFIG,
          autoClose: TOAST_AUTO_CLOSE.SHORT
        })
      } catch (error) {
        console.error('Error updating introductions:', error)
        showErrorToast('Failed to update introductions', {
          ...TOAST_CONFIG,
          autoClose: TOAST_AUTO_CLOSE.DEFAULT
        })
      }
    },
    [contactId, contact?.prog__introductions, formData.prog__introductions, dispatch]
  )

  const handleIntroductionsChange = useCallback((updatedIntroductions: any[]) => {
    const validIntroductions = (updatedIntroductions || []).filter((intro: any) => intro.companyId && intro.categoryId)
    setFormData((prev) => ({
      ...prev,
      prog__introductions: validIntroductions
    }))
  }, [])

  const handleRolesSave = useCallback(
    async (savedRole: any, allRoles: any[], rolesMode: 'current' | 'prior') => {
      const currentToken = getAccessToken(store.getState().auth)
      if (!contactId || !currentToken) {
        showErrorToast('Cannot save roles: Contact ID or access token not available', {
          ...TOAST_CONFIG,
          autoClose: TOAST_AUTO_CLOSE.DEFAULT
        })
        return
      }

      try {
        const currentRolesKey = rolesMode === 'current' ? 'prog__current_roles' : 'prog__prior_roles'
        const currentRoles = contact?.[currentRolesKey] || formData[currentRolesKey] || []

        const newRolesData = allRoles
          .filter((role) => role.companyId && role.categoryId)
          .map((role) => {
            const isNew = role.key.startsWith('new-')
            const originalRole = role.originalRole

            const roleEntry: any = {
              [CompanyTeamEntryFields.contacts__id]: contactId,
              [CompanyTeamEntryFields.contact_accounts__id]: isNew ? null : role.key,
              [CompanyTeamEntryFields.contact_accounts__account_id]: role.companyId,
              [CompanyTeamEntryFields.contact_accounts__relationship_category_id]: role.categoryId,
              [CompanyTeamEntryFields.contact_accounts__current]: rolesMode === 'current',
              [CompanyTeamEntryFields.prog__contact_accounts__action]: isNew
                ? ACTION_TYPE_INSERT
                : originalRole && originalRole[CompanyTeamEntryFields.contact_accounts__id]
                  ? ACTION_TYPE_UPDATE
                  : ''
            }

            if (role.roleTypeId) {
              roleEntry[CompanyTeamEntryFields.relationship_types__id] = role.roleTypeId
            }
            if (role.title !== undefined) {
              roleEntry[CompanyTeamEntryFields.contact_accounts__title] = role.title || null
            }

            if (rolesMode === 'current') {
              roleEntry[CompanyTeamEntryFields.contact_accounts__is_primary] = role.isPrimary === true
            }

            return roleEntry
          })

        const relationshipsByCompany = new Map<string, any[]>()

        const currentRoleMap = new Map<string, any>()
        currentRoles.forEach((role: any) => {
          const companyId =
            role[CompanyTeamEntryFields.contact_accounts__account_id] || role.prog__account_id || role.account_id
          const categoryId =
            role[CompanyTeamEntryFields.contact_accounts__relationship_category_id] ||
            role.contact_accounts__relationship_category_id
          const roleId = role[CompanyTeamEntryFields.contact_accounts__id] || role.contact_accounts__id
          if (companyId && categoryId && roleId) {
            currentRoleMap.set(`${companyId}-${categoryId}`, { companyId, roleId, categoryId })
          }
        })

        const newRoleMap = new Map<string, any>()
        const newRoleMapByRoleId = new Map<string, any>()
        newRolesData.forEach((role) => {
          const companyId = role[CompanyTeamEntryFields.contact_accounts__account_id]
          const categoryId = role[CompanyTeamEntryFields.contact_accounts__relationship_category_id]
          const roleId = role[CompanyTeamEntryFields.contact_accounts__id]
          const action = role[CompanyTeamEntryFields.prog__contact_accounts__action]

          if (companyId && categoryId) {
            newRoleMap.set(`${companyId}-${categoryId}`, role)

            if (roleId) {
              newRoleMapByRoleId.set(roleId, role)
            }

            if (action === ACTION_TYPE_INSERT || action === ACTION_TYPE_UPDATE) {
              const relationships = relationshipsByCompany.get(companyId) || []
              relationships.push(role)
              relationshipsByCompany.set(companyId, relationships)
            }
          }
        })

        currentRoleMap.forEach((currentRole, key) => {
          if (!newRoleMap.has(key)) {
            if (!newRoleMapByRoleId.has(currentRole.roleId)) {
              const relationships = relationshipsByCompany.get(currentRole.companyId) || []
              relationships.push({
                [CompanyTeamEntryFields.contact_accounts__id]: currentRole.roleId,
                [CompanyTeamEntryFields.prog__contact_accounts__action]: ACTION_TYPE_DELETE
              })
              relationshipsByCompany.set(currentRole.companyId, relationships)
            }
          }
        })

        const companiesToProcess: Array<{ companyId: string; relationships: any[] }> = []
        for (const [companyId, relationships] of relationshipsByCompany.entries()) {
          if (relationships.length > 0) {
            companiesToProcess.push({ companyId, relationships })
          }
        }

        if (companiesToProcess.length === 0) {
          setFormData((prev) => ({
            ...prev,
            [currentRolesKey]: newRolesData
          }))
          return
        }

        const results = await Promise.allSettled(
          companiesToProcess.map(({ companyId, relationships }) =>
            processContactCompanyRelationships(contactId, companyId, relationships, currentToken)
          )
        )

        const failures = results.filter(
          (result) => result.status === 'rejected' || (result.status === 'fulfilled' && !result.value.success)
        )
        if (failures.length > 0) {
          const errorMessages = failures.map((failure) => {
            if (failure.status === 'rejected') {
              return failure.reason?.message || 'Unknown error'
            }
            return failure.value.error || 'Unknown error'
          })
          showErrorToast(`Failed to update some roles: ${errorMessages.join(', ')}`, {
            ...TOAST_CONFIG
          })
          return
        }

        const response = await fetchContactById(contactId, currentToken)
        if (response.data && !response.error) {
          dispatch(updateContactRecord({ contactId, updates: response.data }))
          setFormData(response.data)
        }

        toast.success(`${rolesMode === 'current' ? 'Current' : 'Prior'} roles updated successfully`, {
          ...TOAST_CONFIG,
          autoClose: TOAST_AUTO_CLOSE.SHORT
        })
      } catch (error) {
        console.error('Error updating roles:', error)
        showErrorToast('Failed to update roles', {
          ...TOAST_CONFIG,
          autoClose: TOAST_AUTO_CLOSE.DEFAULT
        })
      }
    },
    [contactId, contact, formData, dispatch]
  )

  const handleIntroductionsRemove = useCallback(
    async (removedIntro: any, allIntroductions: any[]) => {
      const currentToken = getAccessToken(store.getState().auth)
      if (!contactId || !currentToken) {
        showErrorToast('Cannot delete introduction: Contact ID or access token not available', {
          ...TOAST_CONFIG,
          autoClose: TOAST_AUTO_CLOSE.DEFAULT
        })
        throw new Error('Contact ID or access token not available')
      }

      try {
        const companyId = removedIntro.companyId
        const relationshipId = removedIntro.key

        if (!companyId) {
          throw new Error('Company ID not found for introduction to delete')
        }

        if (relationshipId && typeof relationshipId === 'string' && relationshipId.startsWith('new-')) {
          return
        }

        if (!relationshipId) {
          throw new Error('Relationship ID not found for introduction to delete')
        }

        const deletePayload = [
          {
            [CompanyTeamEntryFields.contact_accounts__id]: relationshipId,
            [CompanyTeamEntryFields.prog__contact_accounts__action]: ACTION_TYPE_DELETE
          }
        ]

        const result = await processContactCompanyRelationships(contactId, companyId, deletePayload, currentToken)

        if (!result.success) {
          throw new Error(result.error || 'Failed to delete introduction')
        }

        const response = await fetchContactById(contactId, currentToken)
        if (response.data && !response.error) {
          dispatch(updateContactRecord({ contactId, updates: response.data }))
          setFormData(response.data)
        }

        toast.success('Introduction deleted successfully', {
          ...TOAST_CONFIG,
          autoClose: TOAST_AUTO_CLOSE.SHORT
        })
      } catch (error) {
        console.error('Error deleting introduction:', error)
        showErrorToast(error instanceof Error ? error.message : 'Failed to delete introduction', {
          ...TOAST_CONFIG,
          autoClose: TOAST_AUTO_CLOSE.DEFAULT
        })
        throw error
      }
    },
    [contactId, dispatch]
  )

  const handleFounderOfChange = useCallback(
    async (field: string, value: any) => {
      const currentToken = getAccessToken(store.getState().auth)
      if (!contactId || !currentToken) {
        showErrorToast('Cannot save founder_of: Contact ID or access token not available', {
          ...TOAST_CONFIG,
          autoClose: TOAST_AUTO_CLOSE.DEFAULT
        })
        return
      }

      try {
        const currentEntries = contact?.prog__founder_of || formData.prog__founder_of || []
        const newSelections = Array.isArray(value) ? value : []

        const relationshipsByCompany = transformFounderOfToAPIFormat(contactId, currentEntries, newSelections)

        const companiesToProcess: Array<{ companyId: string; relationships: any[] }> = []
        for (const [companyId, relationships] of relationshipsByCompany.entries()) {
          if (relationships.length > 0) {
            companiesToProcess.push({ companyId, relationships })
          }
        }

        if (companiesToProcess.length === 0) {
          toast.info('No changes to save', {
            ...TOAST_CONFIG,
            autoClose: TOAST_AUTO_CLOSE.SHORT
          })
          return
        }

        const results = await Promise.allSettled(
          companiesToProcess.map(({ companyId, relationships }) =>
            processContactCompanyRelationships(contactId, companyId, relationships, currentToken)
          )
        )

        const failures = results.filter(
          (result) => result.status === 'rejected' || (result.status === 'fulfilled' && !result.value.success)
        )
        if (failures.length > 0) {
          const errorMessages = failures.map((failure) => {
            if (failure.status === 'rejected') {
              return failure.reason?.message || 'Unknown error'
            }
            return failure.value.error || 'Unknown error'
          })
          showErrorToast(`Failed to update some founder relationships: ${errorMessages.join(', ')}`, {
            ...TOAST_CONFIG
          })
          return
        }

        const response = await fetchContactById(contactId, currentToken)
        if (response.data && !response.error) {
          dispatch(updateContactRecord({ contactId, updates: response.data }))
          setFormData(response.data)
        }

        toast.success('Founder relationships updated successfully', {
          ...TOAST_CONFIG,
          autoClose: TOAST_AUTO_CLOSE.SHORT
        })
      } catch (error) {
        console.error('Error updating founder_of:', error)
        showErrorToast('Failed to update founder relationship', {
          ...TOAST_CONFIG,
          autoClose: TOAST_AUTO_CLOSE.DEFAULT
        })
      }
    },
    [contactId, contact?.prog__founder_of, formData.prog__founder_of, dispatch]
  )

  const handleFieldChange = useCallback(
    (field: string, value: any) => {
      if (field === 'prog__founder_of') {
        return
      }

      const updates: Record<string, any> = {}

      if (
        field === ContactRecordFields.contacts__address_city_label ||
        field === ContactRecordFields.contacts__address_city_code
      ) {
        const countryCode = value?.data?.countryCode || value?.countryCode
        const cityLabel = value?.name || value?.label || ''

        if (countryCode) {
          updates[ContactRecordFields.contacts__address_country_code] = countryCode
          if (cityLabel) {
            updates[ContactRecordFields.contacts__address_city_label] = cityLabel
          }
          if (countriesData.length > 0) {
            const countryLabel = countriesData.find((country: any) => country.code === countryCode)?.name
            if (countryLabel) {
              updates[ContactRecordFields.contacts__address_country_label] = countryLabel
            }
          }
        }
      }

      if (
        field === ContactRecordFields.contacts__address_country_code ||
        field === ContactRecordFields.contacts__address_country_label
      ) {
        const countryCode = value?.value || value
        const countryLabel = value?.label || value

        updates[ContactRecordFields.contacts__address_city_code] = null
        updates[ContactRecordFields.contacts__address_city_label] = null

        if (countryCode) {
          updates[ContactRecordFields.contacts__address_country_code] = countryCode
        }
        if (countryLabel && countryLabel !== countryCode) {
          updates[ContactRecordFields.contacts__address_country_label] = countryLabel
        } else if (countriesData.length > 0 && countryCode) {
          const countryName = countriesData.find((country: any) => country.code === countryCode)?.name
          if (countryName) {
            updates[ContactRecordFields.contacts__address_country_label] = countryName
          }
        }
      }

      const addressFields: string[] = [
        ContactRecordFields.contacts__address_1,
        ContactRecordFields.contacts__address_2,
        ContactRecordFields.contacts__address_3,
        ContactRecordFields.contacts__address_stateprovince,
        ContactRecordFields.contacts__address_postal_code
      ]
      if (addressFields.includes(field)) {
        updates[field] = value
      }

      if (Object.keys(updates).length > 0 && contactId && contact) {
        dispatch(updateContactRecord({ contactId, updates }))
      }

      setFormData((prev) => {
        const businessRuleResult = handleBusinessRules(field, value, prev, form)
        return businessRuleResult.formData
      })
    },
    [contact, contactId, dispatch, countriesData, form]
  )

  const handlePatchSuccess = useCallback(
    (recordId: string, updates: Record<string, any>) => {
      if (ContactRecordFields.contacts__address_country_code in updates) {
        const countryCode = updates[ContactRecordFields.contacts__address_country_code]
        if (countryCode && countriesData.length > 0) {
          updates[ContactRecordFields.contacts__address_country_label] = countriesData.find(
            (country: any) => country.code === countryCode
          )?.name
        } else {
          updates[ContactRecordFields.contacts__address_country_label] = null
        }
      }

      setFormData((prev) => ({ ...prev, ...updates }))

      dispatch(
        updateContactRecord({
          contactId: recordId,
          updates
        })
      )
    },
    [dispatch, countriesData]
  )

  const helpers = useMemo(
    () => ({
      getLookupOptions: (category: string) => {
        return getLookupOptions(category)
      }
    }),
    [getLookupOptions]
  )

  const contactInfoFields = useMemo(() => {
    if (!contact) return []
    const mergedData = { ...contact, ...formData }
    return getContactInfoFields(mergedData, helpers, handleFounderOfChange)
  }, [contact, formData, helpers, handleFounderOfChange])

  const addressFields = useMemo(() => {
    if (!contact) return []
    const mergedData = { ...contact, ...formData }
    return getAddressFields(mergedData, helpers, false)
  }, [contact, formData, helpers])

  const getCompanyLabelById = useCallback(
    (companyId: string | null | undefined): string => {
      if (!companyId || !filterOptions) return companyId || ''
      const companyOptions = filterOptions['contact_accounts__account_id'] || []
      const option = companyOptions.find((opt: any) => opt.value === companyId)
      return option?.label || option?.name || companyId
    },
    [filterOptions]
  )

  const getCategoryLabelById = useCallback(
    (categoryId: string | null | undefined): string => {
      if (!categoryId) return categoryId || ''

      if (filterOptions) {
        const categoryOptions = filterOptions['contact_accounts__relationship_category_id'] || []
        const option = categoryOptions.find((opt: any) => opt.value === categoryId)
        if (option?.label || option?.name) {
          return option.label || option.name
        }
      }

      if (relationshipCategories && relationshipCategories.length > 0) {
        const category = relationshipCategories.find((cat: any) => cat.id === categoryId)
        if (category?.category) {
          return category.category
        }
      }

      return categoryId
    },
    [filterOptions, relationshipCategories]
  )

  const handleMoveToPrior = useCallback(
    async (entry: CompanyTeamEntry) => {
      const currentToken = getAccessToken(store.getState().auth)
      if (!contactId || !currentToken) {
        showErrorToast('Cannot move role to prior: Contact ID or access token not available', {
          ...TOAST_CONFIG,
          autoClose: TOAST_AUTO_CLOSE.DEFAULT
        })
        return
      }

      try {
        const companyId = entry[CompanyTeamEntryFields.contact_accounts__account_id]
        const relationshipId = entry[CompanyTeamEntryFields.contact_accounts__id]

        if (!companyId || !relationshipId) {
          throw new Error('Company ID or Relationship ID not found')
        }

        const { key, ...entryWithoutKey } = entry as any
        const entryToUpdate: CompanyTeamEntry = {
          ...entryWithoutKey,
          [CompanyTeamEntryFields.contact_accounts__current]: false,
          [CompanyTeamEntryFields.prog__contact_accounts__action]: ACTION_TYPE_UPDATE
        }

        const result = await processContactCompanyRelationships(contactId, companyId, [entryToUpdate], currentToken)

        if (!result.success) {
          throw new Error(result.error || 'Failed to move role to prior')
        }

        const currentRoles = formData.prog__current_roles || contact?.prog__current_roles || []
        const priorRoles = formData.prog__prior_roles || contact?.prog__prior_roles || []

        const finalCurrentRoles = currentRoles.filter(
          (role: CompanyTeamEntry) => role[CompanyTeamEntryFields.contact_accounts__id] !== relationshipId
        )
        const updatedPriorRoles = [...priorRoles, entryToUpdate]

        const updatedFormData = {
          ...formData,
          [ContactRecordFields.prog__current_roles]: finalCurrentRoles,
          [ContactRecordFields.prog__prior_roles]: updatedPriorRoles
        }
        setFormData(updatedFormData)
        dispatch(updateContactRecord({ contactId, updates: updatedFormData }))

        toast.success('Role moved to prior roles successfully', {
          ...TOAST_CONFIG,
          autoClose: TOAST_AUTO_CLOSE.SHORT
        })
      } catch (error) {
        console.error('Error moving role to prior:', error)
        showErrorToast(error instanceof Error ? error.message : 'Failed to move role to prior', {
          ...TOAST_CONFIG,
          autoClose: TOAST_AUTO_CLOSE.DEFAULT
        })
      }
    },
    [contactId, contact, formData, dispatch]
  )

  const handleMoveToCurrent = useCallback(
    async (entry: CompanyTeamEntry) => {
      const currentToken = getAccessToken(store.getState().auth)
      if (!contactId || !currentToken) {
        console.warn('Cannot move role to current: Contact ID or access token not available')
        showErrorToast('Cannot move role to current: Contact ID or access token not available', {
          ...TOAST_CONFIG,
          autoClose: TOAST_AUTO_CLOSE.DEFAULT
        })
        return
      }

      try {
        const companyId = entry[CompanyTeamEntryFields.contact_accounts__account_id]
        const relationshipId = entry[CompanyTeamEntryFields.contact_accounts__id]

        if (!companyId || !relationshipId) {
          throw new Error('Company ID or Relationship ID not found')
        }

        const { key, ...entryWithoutKey } = entry as any
        const entryToUpdate: CompanyTeamEntry = {
          ...entryWithoutKey,
          [CompanyTeamEntryFields.contact_accounts__current]: true,
          [CompanyTeamEntryFields.prog__contact_accounts__action]: ACTION_TYPE_UPDATE
        }

        const result = await processContactCompanyRelationships(contactId, companyId, [entryToUpdate], currentToken)

        if (!result.success) {
          throw new Error(result.error || 'Failed to move role to current')
        }

        const currentRoles = formData.prog__current_roles || contact?.prog__current_roles || []
        const priorRoles = formData.prog__prior_roles || contact?.prog__prior_roles || []

        const finalPriorRoles = priorRoles.filter(
          (role: CompanyTeamEntry) => role[CompanyTeamEntryFields.contact_accounts__id] !== relationshipId
        )
        const updatedCurrentRoles = [...currentRoles, entryToUpdate]

        const updatedFormData = {
          ...formData,
          [ContactRecordFields.prog__current_roles]: updatedCurrentRoles,
          [ContactRecordFields.prog__prior_roles]: finalPriorRoles
        }
        setFormData(updatedFormData)
        dispatch(updateContactRecord({ contactId, updates: updatedFormData }))

        toast.success('Role moved to current roles successfully', {
          ...TOAST_CONFIG,
          autoClose: TOAST_AUTO_CLOSE.SHORT
        })
      } catch (error) {
        console.error('Error moving role to current:', error)
        showErrorToast(error instanceof Error ? error.message : 'Failed to move role to current', {
          ...TOAST_CONFIG,
          autoClose: TOAST_AUTO_CLOSE.DEFAULT
        })
      }
    },
    [contactId, contact, formData, dispatch]
  )

  const handleSetPrimary = useCallback(
    async (entry: CompanyTeamEntry) => {
      const currentToken = getAccessToken(store.getState().auth)
      if (!contactId || !currentToken) {
        console.warn('Cannot set primary role: Contact ID or access token not available')
        showErrorToast('Cannot set primary role: Contact ID or access token not available', {
          ...TOAST_CONFIG,
          autoClose: TOAST_AUTO_CLOSE.DEFAULT
        })
        return
      }

      try {
        const companyId = entry[CompanyTeamEntryFields.contact_accounts__account_id]
        const relationshipId = entry[CompanyTeamEntryFields.contact_accounts__id]

        if (!companyId || !relationshipId) {
          throw new Error('Company ID or Relationship ID not found')
        }

        const currentRoles = formData.prog__current_roles || contact?.prog__current_roles || []
        const rolesForCompany = currentRoles.filter(
          (role: CompanyTeamEntry) => role[CompanyTeamEntryFields.contact_accounts__account_id] === companyId
        )

        const payload = rolesForCompany.map((role: CompanyTeamEntry) => {
          const roleEntry = { ...role }
          if (role[CompanyTeamEntryFields.contact_accounts__id] === relationshipId) {
            roleEntry[CompanyTeamEntryFields.prog__new_is_primary_id] = relationshipId
          } else {
            roleEntry[CompanyTeamEntryFields.prog__new_is_primary_id] = null
          }
          return roleEntry
        })

        const headers: Record<string, string> = {
          'Content-Type': 'application/json'
        }

        if (currentToken) {
          headers['Authorization'] = `Bearer ${currentToken}`
        }

        headers['account-id'] = companyId

        const response = await fetch(
          buildFullUrl(getBackendBaseUrl(), API_ROUTES.CONTACT_RELATIONSHIPS.SET_PRIMARY_CONTACT),
          {
            method: API_ROUTES.CONTACT_RELATIONSHIPS.SET_PRIMARY_CONTACT.method,
            headers,
            body: JSON.stringify(payload)
          }
        )

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.error || `HTTP ${response.status}`)
        }

        const result = await response.json()

        const updatedEntries = result?.data || result
        if (updatedEntries && Array.isArray(updatedEntries)) {
          const updatedRoleIds = new Set(
            updatedEntries.map((role: CompanyTeamEntry) => role[CompanyTeamEntryFields.contact_accounts__id])
          )

          const updatedCurrentRoles = currentRoles.map((role: CompanyTeamEntry) => {
            const roleId = role[CompanyTeamEntryFields.contact_accounts__id]
            if (updatedRoleIds.has(roleId)) {
              const updatedRole = updatedEntries.find(
                (updated: CompanyTeamEntry) => updated[CompanyTeamEntryFields.contact_accounts__id] === roleId
              )
              return updatedRole || role
            }
            return role
          })

          const updatedFormData = {
            ...formData,
            [ContactRecordFields.prog__current_roles]: updatedCurrentRoles
          }
          setFormData(updatedFormData)
          dispatch(updateContactRecord({ contactId, updates: updatedFormData }))
        }

        toast.success('Primary role set successfully', {
          ...TOAST_CONFIG,
          autoClose: TOAST_AUTO_CLOSE.SHORT
        })
      } catch (error) {
        console.error('Error setting primary role:', error)
        showErrorToast(error instanceof Error ? error.message : 'Failed to set primary role', {
          ...TOAST_CONFIG,
          autoClose: TOAST_AUTO_CLOSE.DEFAULT
        })
      }
    },
    [contactId, contact, formData, dispatch]
  )

  const handleDeleteRole = useCallback(
    async (entry: CompanyTeamEntry) => {
      const currentToken = getAccessToken(store.getState().auth)
      if (!contactId || !currentToken) {
        console.warn('Cannot delete role: Contact ID or access token not available')
        showErrorToast('Cannot delete role: Contact ID or access token not available', {
          ...TOAST_CONFIG,
          autoClose: TOAST_AUTO_CLOSE.DEFAULT
        })
        return
      }

      try {
        const companyId = entry[CompanyTeamEntryFields.contact_accounts__account_id]
        const relationshipId = entry[CompanyTeamEntryFields.contact_accounts__id]

        if (!companyId || !relationshipId) {
          throw new Error('Company ID or Relationship ID not found')
        }

        const deletePayload = [
          {
            [CompanyTeamEntryFields.contact_accounts__id]: relationshipId,
            [CompanyTeamEntryFields.prog__contact_accounts__action]: ACTION_TYPE_DELETE
          }
        ]

        const result = await processContactCompanyRelationships(contactId, companyId, deletePayload, currentToken)

        if (!result.success) {
          throw new Error(result.error || 'Failed to delete role')
        }

        const response = await fetchContactById(contactId, currentToken)
        if (response.data && !response.error) {
          dispatch(updateContactRecord({ contactId, updates: response.data }))
          setFormData(response.data)
        }

        toast.success('Role deleted successfully', {
          ...TOAST_CONFIG,
          autoClose: TOAST_AUTO_CLOSE.SHORT
        })
      } catch (error) {
        console.error('Error deleting role:', error)
        showErrorToast(error instanceof Error ? error.message : 'Failed to delete role', {
          ...TOAST_CONFIG,
          autoClose: TOAST_AUTO_CLOSE.DEFAULT
        })
      }
    },
    [contactId, dispatch]
  )

  const getInitials = useCallback(() => {
    if (!contact) return '?'
    const firstName = contact[ContactRecordFields.contacts__first_name] || ''
    const lastName = contact[ContactRecordFields.contacts__last_name] || ''
    const firstInitial = firstName.charAt(0).toUpperCase()
    const lastInitial = lastName.charAt(0).toUpperCase()
    return firstInitial + lastInitial || '?'
  }, [contact])

  const photoUrl =
    contact?.[ContactRecordFields.contacts__photo_url] || formData[ContactRecordFields.contacts__photo_url] || null

  return (
    <div
      data-debug='drawer-existing-container'
      data-component='DrawerExisting'
      data-contact-id={contactId}
      className='h-full overflow-y-auto overflow-x-hidden'
    >
      <div data-debug='drawer-unified-layout' className='flex flex-col h-full'>
        <div data-debug='drawer-overview-compact' className='flex-shrink-0 mb-2'>
          {isDrawerContext ? (
            <div className='space-y-4'>
              <div className='mb-4 flex justify-center'>
                <Avatar
                  size={80}
                  src={photoUrl || undefined}
                  className={photoUrl ? '' : 'bg-blue-500 text-white text-2xl font-bold'}
                >
                  {!photoUrl && getInitials()}
                </Avatar>
              </div>

              <FormSection title={SECTION_CONTACT_OVERVIEW} className='mb-2'>
                <InputManager
                  inputMode='read'
                  labelPosition='inline'
                  fields={contactInfoFields}
                  onChange={async (field, value) => {
                    if (field === 'prog__founder_of') {
                      await handleFounderOfChange(field, value)
                    } else {
                      handleFieldChange(field, value)
                    }
                  }}
                  onPatchSuccess={handlePatchSuccess}
                  recordId={contactId || ''}
                  patchId={contactId || ''}
                  enableAutoPatch={true}
                />
              </FormSection>

              <FormSection title='Address' className='mb-2'>
                <InputManager
                  inputMode='read'
                  labelPosition='inline'
                  fields={addressFields}
                  onChange={(field, value) => handleFieldChange(field, value)}
                  onPatchSuccess={handlePatchSuccess}
                  recordId={contactId || ''}
                  patchId={contactId || ''}
                  enableAutoPatch={true}
                />
              </FormSection>

              <div className='mt-2'>
                <ContactRolesTable
                  key={`current-roles-${currentRolesKey || 'empty'}`}
                  mode='current'
                  roles={formData.prog__current_roles || contact?.prog__current_roles || []}
                  onChange={(roles) => handleFieldChange('prog__current_roles', roles)}
                  contactId={contactId}
                  getCompanyLabelById={getCompanyLabelById}
                  getCategoryLabelById={getCategoryLabelById}
                  onMoveToPrior={handleMoveToPrior}
                  onSetPrimary={handleSetPrimary}
                  onDelete={handleDeleteRole}
                  onSave={(savedRole, allRoles) => handleRolesSave(savedRole, allRoles, 'current')}
                />
              </div>

              <div className='mt-2'>
                <ContactRolesTable
                  key={`prior-roles-${priorRolesKey || 'empty'}`}
                  mode='prior'
                  roles={formData.prog__prior_roles || contact?.prog__prior_roles || []}
                  onChange={(roles) => handleFieldChange('prog__prior_roles', roles)}
                  contactId={contactId}
                  getCompanyLabelById={getCompanyLabelById}
                  getCategoryLabelById={getCategoryLabelById}
                  onMoveToCurrent={handleMoveToCurrent}
                  onDelete={handleDeleteRole}
                  onSave={(savedRole, allRoles) => handleRolesSave(savedRole, allRoles, 'prior')}
                />
              </div>

              <div className='mt-2'>
                <IntroductionsTable
                  key={`introductions-${introductionsKey || 'empty'}`}
                  ref={introductionsRef}
                  mode='existing'
                  contactId={contactId}
                  recordId={contactId}
                  initialIntroductions={formData.prog__introductions || contact?.prog__introductions || []}
                  onChange={handleIntroductionsChange}
                  onSave={handleIntroductionsSave}
                  onRemove={handleIntroductionsRemove}
                />
              </div>
            </div>
          ) : (
            <>
              <Row gutter={12}>
                <Col span={12}>
                  <FormSection title={SECTION_CONTACT_OVERVIEW} className='mb-2'>
                    <InputManager
                      inputMode='read'
                      labelPosition='inline'
                      fields={contactInfoFields}
                      onChange={async (field, value) => {
                        if (field === 'prog__founder_of') {
                          await handleFounderOfChange(field, value)
                        } else {
                          handleFieldChange(field, value)
                        }
                      }}
                      onPatchSuccess={handlePatchSuccess}
                      recordId={contactId || ''}
                      patchId={contactId || ''}
                      enableAutoPatch={true}
                    />
                  </FormSection>
                </Col>

                <Col span={12}>
                  <div className='mb-4 flex justify-center'>
                    <Avatar
                      size={120}
                      src={photoUrl || undefined}
                      className={photoUrl ? '' : 'bg-blue-500 text-white text-3xl font-bold'}
                    >
                      {!photoUrl && getInitials()}
                    </Avatar>
                  </div>
                  <FormSection title='Address' className='mb-2'>
                    <InputManager
                      inputMode='read'
                      labelPosition='inline'
                      fields={addressFields}
                      onChange={(field, value) => handleFieldChange(field, value)}
                      onPatchSuccess={handlePatchSuccess}
                      recordId={contactId || ''}
                      patchId={contactId || ''}
                      enableAutoPatch={true}
                    />
                  </FormSection>
                </Col>
              </Row>

              <div className='mt-2'>
                <ContactRolesTable
                  key={`current-roles-${currentRolesKey || 'empty'}`}
                  mode='current'
                  roles={formData.prog__current_roles || contact?.prog__current_roles || []}
                  onChange={(roles) => handleFieldChange('prog__current_roles', roles)}
                  contactId={contactId}
                  getCompanyLabelById={getCompanyLabelById}
                  getCategoryLabelById={getCategoryLabelById}
                  onMoveToPrior={handleMoveToPrior}
                  onSetPrimary={handleSetPrimary}
                  onDelete={handleDeleteRole}
                  onSave={(savedRole, allRoles) => handleRolesSave(savedRole, allRoles, 'current')}
                />
              </div>

              <div className='mt-2'>
                <ContactRolesTable
                  key={`prior-roles-${priorRolesKey || 'empty'}`}
                  mode='prior'
                  roles={formData.prog__prior_roles || contact?.prog__prior_roles || []}
                  onChange={(roles) => handleFieldChange('prog__prior_roles', roles)}
                  contactId={contactId}
                  getCompanyLabelById={getCompanyLabelById}
                  getCategoryLabelById={getCategoryLabelById}
                  onMoveToCurrent={handleMoveToCurrent}
                  onDelete={handleDeleteRole}
                  onSave={(savedRole, allRoles) => handleRolesSave(savedRole, allRoles, 'prior')}
                />
              </div>

              <div className='mt-2'>
                <IntroductionsTable
                  key={`introductions-${introductionsKey || 'empty'}`}
                  ref={introductionsRef}
                  mode='existing'
                  contactId={contactId}
                  recordId={contactId}
                  initialIntroductions={formData.prog__introductions || contact?.prog__introductions || []}
                  onChange={handleIntroductionsChange}
                  onSave={handleIntroductionsSave}
                  onRemove={handleIntroductionsRemove}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
